import { queryEncode } from '@/utils/condition-query-encoding';
import { isEmptyString } from '@/utils/string';
import { isUnaryFilterOperator, type AttributeFilterState } from '../attribute-filter-state';

/**
 * One attribute condition as a people search by filters sends it.
 *
 * The customers endpoint takes the condition as `queryEncode([condition])` in
 * its `filters` param: the legacy condition-array wire, not the people list's
 * proto envelope. The endpoint reads both; this sends the bytes its other
 * callers send, so every person search asks the same question.
 */

/** The operators whose value may not be left blank. */
const VALUE_REQUIRED_OPERATORS = new Set(['eq', '!eq']);

/** Why the condition cannot be searched with yet; empty when it can. */
export function attributeFilterErrors(condition: AttributeFilterState): string[] {
  const errors: string[] = [];
  if (isEmptyString(condition.field)) {
    errors.push("Attribute can't be blank.");
  }
  if (VALUE_REQUIRED_OPERATORS.has(condition.operator) && isEmptyString(condition.value)) {
    errors.push("Value can't be blank.");
  }
  return errors;
}

/**
 * The errors as one sentence for the Apply tooltip: each message's full stop
 * dropped, comma-separated, and one full stop at the end.
 */
export function attributeFilterErrorMessage(errors: readonly string[]): string {
  if (errors.length === 0) {
    return '';
  }
  return `${errors.map((error) => error.replace(/\.$/, '')).join(', ')}.`;
}

/**
 * The `filters` param for a condition search: a one-row legacy condition
 * array, Base64-encoded.
 *
 * The row keeps the legacy wire's key order and spelling — the operator in its
 * segment vocabulary with any negation as a `!` prefix (no `inverse` flag), the
 * field and value trimmed, and a null value for an operator that takes none.
 */
export function encodeLegacyAttributeFilter(condition: AttributeFilterState): string {
  const row = {
    field: condition.field.trim(),
    operator: condition.operator,
    value: isUnaryFilterOperator(condition.operator) ? null : (condition.value?.trim() ?? null),
    type: 'attribute',
  };
  return queryEncode([row]);
}
